import { create, type StateCreator } from "zustand";
import { persist, devtools } from "zustand/middleware";
import { createAuthGuardedStorage, createAuthSlice, type AuthSlice } from "@njbsoft/store";
import { createThemeSlice, type ThemeSlice } from "@njbsoft/store";
import { createLocaleSlice, type LocaleSlice } from "@njbsoft/store";

// The portal's only navigation context: which of the caller's account
// memberships the user is currently viewing. The selection is a SERVER scope —
// every list read threads it as the `account_id` query param and the API
// narrows through the per-account definer helper, so switching accounts
// refetches. Null means the selection has not been made yet (the layout
// resolves and re-pins it to a live membership on mount). The customer
// principal is org-less, so there is NO org slice here.
export interface CustomerContextSlice {
  activeAccountId: string | null;
  setActiveAccount: (id: string | null) => void;
  clearActiveContext: () => void;
}

const createCustomerContextSlice: StateCreator<CustomerStore, [], [], CustomerContextSlice> = (
  set,
) => ({
  activeAccountId: null,
  setActiveAccount: (id) => set({ activeAccountId: id }),
  clearActiveContext: () => set({ activeAccountId: null }),
});

type CustomerStore = AuthSlice & ThemeSlice & LocaleSlice & CustomerContextSlice;

/** The localStorage key the persist middleware writes — the channel sibling
 *  tabs observe each other's token rotations and sign-outs through. */
export const CUSTOMER_STORE_PERSIST_KEY = "njbsoft-customer-store";

/** What survives a reload. Lifted out of the persist options so the
 *  guarded storage can be typed on the PERSISTED shape rather than the whole
 *  store — the two must agree, and deriving one from the other is what keeps
 *  them agreeing. */
const partializeSession = (state: CustomerStore) => ({
  // Access + opaque refresh persisted per the existing web posture
  // (security.md threat model). Customer tokens are short-lived;
  // refresh is server-revocable.
  token: state.token,
  refreshToken: state.refreshToken,
  expiresAt: state.expiresAt,
  // Ordered against what is already persisted before every write.
  rotatedAt: state.rotatedAt,
  user: state.user,
  // The theme follows the OS and the accent is the active utility's brand, so
  // the theme slice's preference and accent are never persisted.
  localePreference: state.localePreference,
  activeAccountId: state.activeAccountId,
});
type PersistedCustomerStore = ReturnType<typeof partializeSession>;

/** The session's persist channel, guarded so no write can move the token pair
 *  BACKWARDS (see `@njbsoft/store`'s auth-persist). */
const authStorage = createAuthGuardedStorage<PersistedCustomerStore>();

export const useCustomerStore = create<CustomerStore>()(
  // devtools enabled only in development to avoid leaking state in production
  devtools(
    persist(
      (...a) => ({
        ...createAuthSlice(...a),
        ...createThemeSlice(...a),
        ...createLocaleSlice(...a),
        ...createCustomerContextSlice(...a),
      }),
      {
        name: CUSTOMER_STORE_PERSIST_KEY,
        storage: authStorage,
        partialize: partializeSession,
        // zustand types `persisted` as `unknown` (the rehydrated localStorage
        // blob); we narrow it to the partialized subset we actually wrote.
        merge: (persisted, current) => ({
          ...current,
          ...(persisted as Partial<CustomerStore>),
          isAuthenticated: !!(persisted as Partial<CustomerStore>)?.refreshToken,
        }),
      },
    ),
    { name: "SAMS v2 Customer", enabled: import.meta.env.DEV },
  ),
);

// The adapter refuses a write that would move the token pair backwards and
// hands back the pair that survived; this catches THIS tab's memory up to it,
// so the context that lost the race is not left holding a spent pair to replay.
authStorage.onKeptNewer((tokens) => {
  useCustomerStore.getState().adoptTokens(tokens);
});
